import React, { useEffect, useState } from "react";
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Textarea,
    useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useNavigate } from "react-router-dom";
import { breathalyzersService } from "../../../services/breathalyzersService";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import Loading from "components/Loading/Loading";
import { checkErrors, hasError } from "utils/forms-utils";

function BreathalyzerNew({ data }) {
    const textColor = useColorModeValue("gray.700", "white");
    const bgTextarea = useColorModeValue("white", "navy.900");
    const placeholderColor = useColorModeValue("gray.300", "gray.400");

    const { t } = useTranslation();
    const navigate = useNavigate();
    const toast = useCustomToast();

    const [formData, setFormData] = useState();
    const [inputError, setInputError] = useState({});

    useEffect(() => {
        initFormData(data);
    }, [data]);

    const initFormData = (values) => {
        setFormData({
            brand: {
                validations: [],
                value: values?.brand || ""
            },
            model: {
                validations: [],
                value: values?.model || ""
            },
            serialNumber: {
                validations: [{ required: true }],
                value: values?.serialNumber || ""
            },
            observations: {
                validations: [],
                value: values?.observations || ""
            }
        });
    };

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        const updatedFormData = {
            ...formData,
            [name]: { ...formData[name], value }
        };
        setInputError(
            checkErrors({ formData, inputError, values: updatedFormData, name })
        );
        setFormData(updatedFormData);
    };

    const toDto = () => ({
        marca: formData.brand.value,
        modelo: formData.model.value,
        numero_serie: formData.serialNumber.value,
        observacoes: formData.observations.value
    });

    const onSave = async (e) => {
        e.preventDefault();

        if (hasError({ formData, inputError })) {
            return;
        }

        try {
            const dataToSave = toDto();

            if (data) {
                await breathalyzersService.update(data.id, dataToSave);
            } else {
                await breathalyzersService.create(dataToSave);
            }

            navigate(ROUTES.breathalyzersPath);

            toast({
                title: t(`settings.breathalyzer.messages.success.${data ? "edit" : "create"}`),
                status: "success"
            });
        } catch (error) {
            toast({
                title: t("system.notifications.error"),
                description: t(error.message),
                status: "error"
            });
        }
    };

    return (
        <Flex
            direction="column"
            minH="89vh"
            align="center"
            pt={{ sm: "120px", md: "75px" }}
        >
            {formData ? (
                <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                    <ListPageCardHeader
                        title={t(`settings.breathalyzer.form.title${data ? "-edit" : ""}`)}
                        subtitle={t(
                            `settings.breathalyzer.form.subtitle${data ? "-edit" : ""}`
                        )}
                    />
                    <CardBody px="22px">
                        <Flex direction="column" w="100%">
                            <Grid
                                templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)" }}
                                gap="24px"
                            >
                                <FormControl isInvalid={inputError["brand"]}>
                                    <FormLabel fontWeight="bold" fontSize="xs">
                                        {t("settings.breathalyzer.form.fields.brand.label")}
                                    </FormLabel>
                                    <Input
                                        variant="main"
                                        name="brand"
                                        value={formData.brand.value}
                                        onChange={handleChange}
                                        placeholder={t(
                                            "settings.breathalyzer.form.fields.brand.placeholder"
                                        )}
                                        fontSize="sm"
                                        maxLength={t(
                                            "settings.breathalyzer.form.fields.brand.max-size"
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError["brand"])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl isInvalid={inputError["model"]}>
                                    <FormLabel fontWeight="bold" fontSize="xs">
                                        {t("settings.breathalyzer.form.fields.model.label")}
                                    </FormLabel>
                                    <Input
                                        variant="main"
                                        name="model"
                                        value={formData.model.value}
                                        onChange={handleChange}
                                        placeholder={t(
                                            "settings.breathalyzer.form.fields.model.placeholder"
                                        )}
                                        fontSize="sm"
                                        maxLength={t(
                                            "settings.breathalyzer.form.fields.model.max-size"
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError["model"])}
                                    </FormErrorMessage>
                                </FormControl>

                                <FormControl
                                    isRequired
                                    isInvalid={inputError["serialNumber"]}
                                >
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t(
                                            "settings.breathalyzer.form.fields.serial-number.label"
                                        )}
                                    </FormLabel>
                                    <Input
                                        variant="main"
                                        name="serialNumber"
                                        value={formData.serialNumber.value}
                                        onChange={handleChange}
                                        placeholder={t(
                                            "settings.breathalyzer.form.fields.serial-number.placeholder"
                                        )}
                                        fontSize="sm"
                                        maxLength={t(
                                            "settings.breathalyzer.form.fields.serial-number.max-size"
                                        )}
                                    />
                                    <FormErrorMessage>
                                        {t(inputError["serialNumber"])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Grid>

                            <Grid mt={"24px"} mb={"24px"} gap="24px">
                                <FormControl isInvalid={inputError["observations"]}>
                                    <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                                        {t("settings.breathalyzer.form.fields.observations.label")}
                                    </FormLabel>
                                    <Textarea
                                        minH="120px"
                                        name="observations"
                                        value={formData.observations.value}
                                        onChange={handleChange}
                                        bg={bgTextarea}
                                        _placeholder={{ color: placeholderColor }}
                                        placeholder={t(
                                            "settings.breathalyzer.form.fields.observations.placeholder"
                                        )}
                                        fontSize="sm"
                                    />
                                    <FormErrorMessage>
                                        {t(inputError["observations"])}
                                    </FormErrorMessage>
                                </FormControl>
                            </Grid>

                            <Flex justify="space-between">
                                <Button
                                    variant="no-effects"
                                    alignSelf="flex-end"
                                    w="125px"
                                    bg="gray.100"
                                    color="gray.700"
                                    fontSize="sm"
                                    onClick={() => navigate(ROUTES.breathalyzersPath)}
                                >
                                    {t(
                                        `settings.breathalyzer.form.buttons.${data ? "cancel" : "back"
                                        }`
                                    )}
                                </Button>
                                <Button
                                    w="125px"
                                    variant="dark"
                                    fontSize="sm"
                                    disabled={hasError({ formData, inputError })}
                                    alignSelf="flex-end"
                                    type="submit"
                                    _hover={{
                                        bg: "grey.500",
                                        color: "white"
                                    }}
                                    onClick={onSave}
                                >
                                    {t(
                                        `settings.breathalyzer.form.buttons.${data ? "update" : "save"
                                        }`
                                    )}
                                </Button>
                            </Flex>
                        </Flex>
                    </CardBody>
                </Card>
            ) : (
                <Loading h="300px" w="100%" />
            )}
        </Flex>
    );
};

export default BreathalyzerNew;